/**
 * Lógica pura de transiciones de estado de Muestra.
 * Sin dependencias React — facilita testing con fast-check (PBT).
 *
 * Requirements: M4.5.6
 */

import type { EstadoMuestra } from "@/hooks/use-muestras";

// =============================================================================
// MAPA DE TRANSICIONES
// Sincronizar con TRANSICIONES_MUESTRA en backend/apps/muestras/constants.py
// =============================================================================

export const TRANSICIONES_MUESTRA: Record<EstadoMuestra, EstadoMuestra[]> = {
  PENDIENTE: ["TOMADA", "RECHAZADA"],
  TOMADA: ["INSUFICIENTE", "RECHAZADA"],
  INSUFICIENTE: ["REPETIDA"],
  RECHAZADA: ["REPETIDA"],
  REPETIDA: [],
};

// =============================================================================
// FUNCIONES PURAS
// =============================================================================

/**
 * Retorna los estados destino válidos desde un estado actual.
 */
export function getTransicionesValidas(estado: EstadoMuestra): EstadoMuestra[] {
  return TRANSICIONES_MUESTRA[estado] ?? [];
}

/**
 * Verifica si una transición de estado es válida.
 */
export function puedeTransicionar(actual: EstadoMuestra, destino: EstadoMuestra): boolean {
  return getTransicionesValidas(actual).includes(destino);
}

/**
 * Retorna las acciones disponibles para una muestra según su estado.
 */
export function getAccionesDisponibles(
  estado: EstadoMuestra
): Array<"tomar" | "rechazar" | "insuficiente" | "repetir" | "etiqueta"> {
  const acciones: Record<
    EstadoMuestra,
    Array<"tomar" | "rechazar" | "insuficiente" | "repetir" | "etiqueta">
  > = {
    PENDIENTE: ["tomar", "rechazar"],
    TOMADA: ["insuficiente", "rechazar", "etiqueta"],
    INSUFICIENTE: ["repetir"],
    RECHAZADA: ["repetir"],
    REPETIDA: ["etiqueta"],
  };
  return acciones[estado] ?? [];
}

/**
 * Verifica si una muestra está en un estado terminal.
 */
export function esEstadoTerminal(estado: EstadoMuestra): boolean {
  return getTransicionesValidas(estado).length === 0;
}

/**
 * Verifica si una muestra tiene estado "definido" (no PENDIENTE).
 * Usado para verificar completitud antes del cierre.
 */
export function esEstadoDefinido(estado: EstadoMuestra): boolean {
  return estado !== "PENDIENTE";
}

/**
 * Retorna el color CSS asociado a cada estado de muestra.
 */
export function getColorEstadoMuestra(estado: EstadoMuestra): string {
  const colores: Record<EstadoMuestra, string> = {
    PENDIENTE: "text-gray-500",
    TOMADA: "text-green-600",
    INSUFICIENTE: "text-yellow-600",
    RECHAZADA: "text-red-600",
    REPETIDA: "text-blue-600",
  };
  return colores[estado] ?? "text-gray-500";
}

/**
 * Retorna el variant de Badge para cada estado de muestra.
 */
export function getBadgeVariantMuestra(
  estado: EstadoMuestra
): "default" | "secondary" | "destructive" | "outline" {
  const variantes: Record<EstadoMuestra, "default" | "secondary" | "destructive" | "outline"> = {
    PENDIENTE: "secondary",
    TOMADA: "default",
    INSUFICIENTE: "outline",
    RECHAZADA: "destructive",
    REPETIDA: "outline",
  };
  return variantes[estado] ?? "secondary";
}
